/**
 * The device side of sync (sync.md §4–§11): `createSyncArea(ctx, transport)`.
 *
 * One cycle = pull until `more = false`, then push until the queue is empty (§7). One cycle at a time;
 * a request made during a cycle sets a "run again" flag. The engine owns no timers: the host calls
 * `cycle()` / `engine.sync.now()` on its timer, on focus / `online`, and `sync.push_debounce_ms` after a
 * local change (web.md "Timers").
 *
 * Pulled changes are merged on the raw driver by `mergeChanges` (sync/apply.ts) in one transaction
 * together with the new cursor; afterwards the store hears about it (`noteExternalWrite`: counts,
 * usefulness buckets, `changed` events with origin 'sync') and the local clock receives the remote
 * Hlcs (`receiveHlc`).
 *
 * Persistent state lives in `sync_meta`: `cursor`, `epoch`, `account`, `needs_full_pull`,
 * `last_synced_at`, `probe_since`. Whether sync is running is in memory: the host calls `sync.start` after
 * sign-in.
 *
 * `probe_since` closes a hole in the tombstone horizon (sync.md §9): the server lets a pull from 0 through
 * whatever its horizon is ("a fresh device"), but a database at cursor 0 is not always fresh — it may have
 * PUSHED rows while its cursor was 0 (its first pull found an empty replica), or be re-pulling everything
 * (`needs_full_pull`) with rows from an older cursor. If the server meanwhile purged the tombstone of such a
 * row, a pull from 0 cannot say so: the device kept the row live and its next push of that row
 * (`missing_base` → the whole row) resurrected it on the server, while a device that had seen the stub kept
 * the stub (found by the convergence property test, seed 50000 schedule 17). So such a database remembers
 * the cursor it must be judged by — 1 after a push at cursor 0 (anything the server deleted later has a
 * higher sequence number than what was pushed), the old cursor before a full re-pull — and before pulling
 * from 0 asks the server once with `since = probe_since`: `resync_required` means wipe and download, exactly
 * as for a device away too long. The probe is cleared as soon as the cursor moves past 0.
 */

import type { DeviceId } from '../contracts/ids.js';
import type { RuntimeKind } from '../contracts/platform.js';
import {
  SCHEMA_VERSION, SYNC_TABLES, type DeviceInfo, type PullResponse, type PushResponse, type RowChange, type SyncPhase,
  type SyncStatus, type SyncTableName, type SyncTableSpec, type SyncTransport,
} from '../contracts/sync.js';
import type { SyncArea } from '../contracts/wiring.js';
import type { EngineContext } from '../engine/context.js';
import { DigesterError, toDigesterError } from '../kernel/errors.js';
import {
  clearDirtyIfMark, countDirty, currentMark, deleteSyncedRow, dirtyOfRow, dropDirtyRow, keyFromWireId, markEverythingDirty,
  markRegistersDirty, messageLife, oldestDirty, selectSyncedRow, wipeSyncedTables, type QueuedRegister,
} from '../storage/ext/sync/index.js';
import { childWireIds } from '../storage/cascade.js';
import { recomputeShownApplied } from '../storage/shown.js';
import { TABLE_META } from '../storage/meta.js';
import { MAX_TRACKED_MESSAGE_IDS } from '../storage/store.js';
import { deleteSyncMeta, getSyncMeta, setSyncMeta } from '../storage/sync-meta.js';
import { mergeChanges } from './apply.js';
import { registerOf, utf8Length } from './registers.js';

const ALL_SYNCED_TABLES: string[] = (SYNC_TABLES as readonly SyncTableSpec[]).map((s) => s.table);

/** Registers per row at most (messages have five). */
const MAX_REGISTERS_PER_ROW = Math.max(...(SYNC_TABLES as readonly SyncTableSpec[]).map((s) => s.registers.length));

/** Restarts of a pull from 0 (epoch change, resync, full pull) allowed within one cycle. */
const MAX_PULL_RESTARTS = 3;

function platformOf(runtime: RuntimeKind): DeviceInfo['platform'] {
  if (runtime === 'server') throw new DigesterError('internal', 'The server replica does not run the device sync loop');
  return runtime;
}

interface PushBatch {
  changes: RowChange[];
  /** Every queued register sent, with the mark it had when read. */
  registers: QueuedRegister[];
  /** Rows whose change carried every register of the table. */
  whole: Set<string>;
}

const rowKey = (tbl: string, id: string): string => `${tbl}\u0000${id}`;

/** Host hooks of the sync loop. */
export interface SyncHooks {
  /**
   * Runs right after every complete pull, before the push, synchronously (no other sync step in
   * between): the moment this database's view equals the server's plus its own unpushed changes. The
   * engine runs the deferred retention sweep here (sync/retention-gate.ts, review finding R1).
   */
  afterPull?: () => void;
}

export function createSyncArea(ctx: EngineContext, transport: SyncTransport | null, hooks: SyncHooks = {}): SyncArea {
  const db = ctx.db;
  let started = false;
  let phase: SyncPhase = 'off';
  let error: SyncStatus['error'] = null;
  let running: Promise<void> | null = null;
  let again = false;
  /** Server rejections seen during the current cycle (reported in status.error). */
  let rejections: Array<{ t: string; id: string; reason: string }> = [];

  const meta = (key: string): string | null => getSyncMeta(db, key);
  const cursor = (): number => Number(meta('cursor') ?? '0');

  const status = (): SyncStatus => {
    const last = meta('last_synced_at');
    return { phase, pending: countDirty(db), lastSyncedAt: last === null ? null : Number(last), error };
  };

  const emitStatus = (): void => {
    ctx.emit({ type: 'sync', status: status() });
  };

  const setPhase = (next: SyncPhase, err: SyncStatus['error'] = null): void => {
    const changed = next !== phase || JSON.stringify(err) !== JSON.stringify(error);
    phase = next;
    error = err;
    if (changed) emitStatus();
  };

  const entitled = (): boolean => ctx.entitlement().limits.syncDevices > 0;

  const requireTransport = (): SyncTransport => {
    if (!transport) throw new DigesterError('unauthenticated', 'Sign in to sync this device.');
    return transport;
  };

  const notifyWipe = (): void => {
    ctx.store.noteExternalWrite({ tables: ALL_SYNCED_TABLES, messageIds: null, origin: 'sync' });
  };

  // ── Pull ──────────────────────────────────────────────────────────────────────────────────────

  /** The replica was reset (sync.md §10): forget the cursor, re-queue everything, adopt the epoch. */
  const adoptNewEpoch = (epoch: string): void => {
    db.transaction(() => {
      setSyncMeta(db, 'cursor', '0');
      deleteSyncMeta(db, 'needs_full_pull');
      deleteSyncMeta(db, 'probe_since');
      setSyncMeta(db, 'epoch', epoch);
      markEverythingDirty(db);
    });
    console.log('[sync] the server replica was reset; pushing this device\'s data again');
  };

  /** The device is older than the tombstone horizon (sync.md §9): wipe synced tables, pull from 0. */
  const resync = (): void => {
    setPhase('resync');
    db.transaction(() => {
      wipeSyncedTables(db);
      setSyncMeta(db, 'cursor', '0');
      deleteSyncMeta(db, 'needs_full_pull');
      deleteSyncMeta(db, 'probe_since');
    });
    notifyWipe();
    console.log('[sync] resync: local synced data wiped, downloading everything');
    setPhase('pulling');
  };

  /**
   * Child-set rule (replica.ts collectPull): for every message whose life register is in this page and
   * that is live here, the page carries ALL of its children on the server. Children of it held here
   * that are neither in that set nor waiting to be pushed are gone on the server (cascaded by a stub
   * this device never saw, or dropped there): remove them, as the cascade would have. Children the
   * server has are applied by the merge itself (re-inserting any that a lost local delete cascaded).
   */
  const reconcileChildren = (changes: readonly RowChange[]): { removed: number; messageIds: string[]; tables: string[] } => {
    const lifeIds = changes.filter((c) => c.t === 'messages' && c.regs?.life !== undefined).map((c) => c.id);
    if (lifeIds.length === 0) return { removed: 0, messageIds: [], tables: [] };
    const incoming = new Set(changes.map((c) => rowKey(c.t, c.id)));
    let removed = 0;
    const messageIds = new Set<string>();
    const tables = new Set<string>();
    for (const messageId of lifeIds) {
      if (messageLife(db, messageId) !== 'live') continue;
      for (const [table, ids] of Object.entries(childWireIds(db, messageId))) {
        const spec = TABLE_META.get(table);
        if (!spec) continue;
        for (const childId of ids) {
          if (incoming.has(rowKey(table, childId)) || dirtyOfRow(db, table, childId).length > 0) continue;
          const key = keyFromWireId(db, spec, childId);
          if (!key) continue;
          removed += deleteSyncedRow(db, spec, key, childId);
          messageIds.add(messageId);
          tables.add(table);
        }
      }
    }
    return { removed, messageIds: [...messageIds], tables: [...tables] };
  };

  /** Apply one pulled page and move the cursor, in one transaction. */
  const applyPage = (res: PullResponse, since: number): void => {
    const knownEpoch = meta('epoch');
    const { outcome, reconciled } = db.transaction(() => {
      const o = mergeChanges(db, res.changes, { now: ctx.now(), maxClockSkewMs: null, allowServerOwnedTables: true });
      const r = reconcileChildren(res.changes);
      // Score rows the child-set rule removed: the shown scores follow what is left (in place, like the applier).
      if (r.tables.includes('message_scores')) for (const id of r.messageIds) recomputeShownApplied(db, id, { force: true });
      // A partial change for a row we do not have, or a child of a message we do not have, cannot
      // happen under the completeness rules; if it does, restart from 0 next cycle (sync.md §6).
      // A full pull (since 0) has nothing better to do.
      const gap = o.rejected.some((x) => x.reason === 'missing_base') || o.orphans.some((x) => x.parent === 'unknown');
      if (since > 0 && gap) setSyncMeta(db, 'needs_full_pull', '1');
      setSyncMeta(db, 'cursor', String(res.next));
      // Past 0, the cursor itself meets the horizon from now on.
      if (res.next > 0) deleteSyncMeta(db, 'probe_since');
      if (knownEpoch === null) setSyncMeta(db, 'epoch', res.epoch);
      return { outcome: o, reconciled: r };
    });
    for (const r of outcome.rejected) {
      if (r.reason !== 'missing_base') console.log(`[sync] ignored a pulled ${r.t} change: ${r.reason}`);
    }
    if (outcome.maxHlc !== null) ctx.store.receiveHlc(outcome.maxHlc);
    const tables = [...new Set([...outcome.tables, ...reconciled.tables])];
    if (tables.length > 0) {
      const ids = new Set([...outcome.messageIds, ...reconciled.messageIds]);
      const overflow = outcome.messageOverflow || ids.size > MAX_TRACKED_MESSAGE_IDS;
      ctx.store.noteExternalWrite({ tables, messageIds: overflow ? null : [...ids], origin: 'sync' }, outcome.buckets);
    }
  };

  const pullAll = async (t: SyncTransport): Promise<void> => {
    setPhase('pulling');
    if (meta('needs_full_pull') !== null) {
      db.transaction(() => {
        // The rows held now came with the old cursor: the re-pull from 0 is judged by it (probe_since).
        const old = cursor();
        if (old > 0) setSyncMeta(db, 'probe_since', String(Math.max(old, Number(meta('probe_since') ?? '0'))));
        setSyncMeta(db, 'cursor', '0');
        deleteSyncMeta(db, 'needs_full_pull');
      });
    }
    const limit = ctx.config.get('sync.batch_rows');
    let restarts = 0;
    const probe = meta('probe_since');
    if (cursor() === 0 && probe !== null) {
      try {
        await t.pull({ since: Number(probe), limit: 1, schemaVersion: SCHEMA_VERSION });
      } catch (e) {
        const err = toDigesterError(e);
        if (err.code !== 'resync_required') throw err;
        restarts++;
        resync();
      }
    }
    for (;;) {
      const since = cursor();
      let res: PullResponse;
      try {
        res = await t.pull({ since, limit, schemaVersion: SCHEMA_VERSION });
      } catch (e) {
        const err = toDigesterError(e);
        if (err.code !== 'resync_required' || restarts >= MAX_PULL_RESTARTS) throw err;
        restarts++;
        resync();
        continue;
      }
      const knownEpoch = meta('epoch');
      if (knownEpoch !== null && res.epoch !== knownEpoch) {
        if (restarts >= MAX_PULL_RESTARTS) throw new DigesterError('internal', 'The server replica keeps changing its epoch');
        restarts++;
        adoptNewEpoch(res.epoch);
        continue;
      }
      applyPage(res, since);
      if (!res.more) return;
    }
  };

  // ── Push ──────────────────────────────────────────────────────────────────────────────────────

  /** Build the next push batch from the queue (synchronous; drops queue entries of rows that are gone). */
  const collectBatch = (maxRows: number, maxBytes: number): PushBatch => {
    const batch: PushBatch = { changes: [], registers: [], whole: new Set() };
    const taken = new Set<string>();
    let bytes = 0;
    let full = false;

    /** Add one row (all its queued registers); false when the batch is full. */
    const addRow = (tbl: string, id: string): boolean => {
      const k = rowKey(tbl, id);
      if (taken.has(k)) return true;
      const m = TABLE_META.get(tbl);
      const key = m ? keyFromWireId(db, m, id) : null;
      const stored = m && key ? selectSyncedRow(db, m, key) : undefined;
      if (!m || !stored) {
        dropDirtyRow(db, tbl, id); // purged or never existed: nothing to send (sync.md §5)
        return true;
      }
      // A child goes after its parent message when the parent is queued too (the server needs it first).
      if (m.parentMessageColumn) {
        const parentId = String(stored[m.parentMessageColumn]);
        if (!taken.has(rowKey('messages', parentId)) && dirtyOfRow(db, 'messages', parentId).length > 0) {
          if (!addRow('messages', parentId)) return false;
        }
      }
      const queued = dirtyOfRow(db, tbl, id);
      const regs: RowChange['regs'] = {};
      for (const q of queued) {
        const reg = registerOf(m, stored, q.grp);
        if (reg) regs[q.grp] = reg;
      }
      if (Object.keys(regs).length === 0) {
        dropDirtyRow(db, tbl, id);
        return true;
      }
      const change: RowChange = { t: tbl as SyncTableName, id, regs };
      const size = utf8Length(JSON.stringify(change)) + 1;
      if (batch.changes.length > 0 && (bytes + size > maxBytes || taken.size >= maxRows)) {
        full = true;
        return false;
      }
      taken.add(k);
      bytes += size;
      batch.changes.push(change);
      batch.registers.push(...queued);
      if (Object.keys(regs).length === m.registers.length) batch.whole.add(k);
      return true;
    };

    // A scan of maxRows × (registers per row) queue entries always names at least maxRows rows, so one
    // scan fills the batch — unless rows turned out to be gone, in which case their entries were
    // dropped and the next scan sees fresh ones.
    for (;;) {
      const scan = oldestDirty(db, maxRows * MAX_REGISTERS_PER_ROW);
      if (scan.length === 0) break;
      for (const reg of scan) {
        if (taken.has(rowKey(reg.tbl, reg.id))) continue;
        if (!addRow(reg.tbl, reg.id) || taken.size >= maxRows) {
          full = true;
          break;
        }
      }
      if (full || batch.changes.length > 0) break;
    }
    return batch;
  };

  /** Settle a push response: clear what the server took, re-queue `missing_base`, drop rejects. */
  const settlePush = (batch: PushBatch, res: PushResponse): number => {
    const rejected = new Set(res.rejected.map((r) => rowKey(r.t, r.id)));
    let progress = 0;
    db.transaction(() => {
      if (meta('epoch') === null) setSyncMeta(db, 'epoch', res.epoch);
      for (const reg of batch.registers) {
        if (rejected.has(rowKey(reg.tbl, reg.id))) continue;
        progress += clearDirtyIfMark(db, reg);
      }
      for (const r of res.rejected) {
        const spec = TABLE_META.get(r.t);
        if (r.reason === 'missing_base' && spec && !batch.whole.has(rowKey(r.t, r.id))) {
          // Send the whole row next time (sync.md §5).
          markRegistersDirty(db, r.t, r.id, spec.registers.map((x) => x.name), currentMark(db));
          progress++;
          continue;
        }
        progress += dropDirtyRow(db, r.t, r.id);
        rejections.push({ t: r.t, id: r.id, reason: r.reason });
      }
    });
    return progress;
  };

  /** Push until the queue is empty. Returns true when the server's epoch changed (cycle again). */
  const pushAll = async (t: SyncTransport): Promise<boolean> => {
    setPhase('pushing');
    const maxRows = ctx.config.get('sync.batch_rows');
    const maxBytes = ctx.config.get('sync.max_batch_bytes');
    for (;;) {
      const batch = collectBatch(maxRows, maxBytes);
      if (batch.changes.length === 0) return false;
      const res = await t.push({ schemaVersion: SCHEMA_VERSION, changes: batch.changes });
      const knownEpoch = meta('epoch');
      if (knownEpoch !== null && res.epoch !== knownEpoch) {
        adoptNewEpoch(res.epoch);
        return true;
      }
      // Registers the server took from a database that has not pulled them back: judge its next pull
      // from 0 as if its cursor were 1 (see the header). A push the server found stale changed nothing there.
      if (res.applied > 0 && cursor() === 0 && meta('probe_since') === null) setSyncMeta(db, 'probe_since', '1');
      // No progress (every register re-dirtied while the request was in flight): leave the rest for
      // the next cycle rather than spin.
      if (settlePush(batch, res) === 0) return false;
    }
  };

  // ── The cycle ─────────────────────────────────────────────────────────────────────────────────

  const handleError = (e: unknown): void => {
    const err = toDigesterError(e);
    switch (err.code) {
      case 'fetch_failed':
        setPhase('offline');
        return;
      case 'not_entitled':
      case 'unauthenticated':
        // Plan lapsed, signed out or account deleted (sync.md §11): stop; local data stays.
        started = false;
        setPhase('off');
        return;
      case 'forbidden':
        // Revoked device: it must register again (sync.start).
        started = false;
        setPhase('error', { code: err.code, message: err.message });
        return;
      default:
        setPhase('error', { code: err.code, message: err.message });
    }
  };

  const runOnce = async (t: SyncTransport): Promise<void> => {
    if (!entitled()) {
      started = false;
      setPhase('off');
      return;
    }
    rejections = [];
    try {
      await pullAll(t);
      if (hooks.afterPull) {
        try {
          hooks.afterPull();
        } catch (e) {
          const err = toDigesterError(e);
          console.log(`[sync] after-pull housekeeping failed (${err.code}): ${err.message}`);
        }
      }
      const epochChanged = await pushAll(t);
      if (epochChanged) {
        again = true;
        return;
      }
      setSyncMeta(db, 'last_synced_at', String(ctx.now()));
      if (rejections.length > 0) {
        const first = rejections[0] as { t: string; reason: string };
        setPhase('error', {
          code: first.reason,
          message: `The server refused ${rejections.length} change(s) (${first.reason} on ${first.t}); they were dropped from the queue.`,
        });
      } else {
        setPhase('idle');
      }
    } catch (e) {
      handleError(e);
    }
  };

  const cycle = (): Promise<void> => {
    const t = transport;
    if (!started || !t) return Promise.resolve();
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      try {
        do {
          again = false;
          await runOnce(t);
        } while (again && started);
      } finally {
        running = null;
      }
    })();
    return running;
  };

  return {
    sync: {
      async status() {
        return status();
      },

      async start(deviceName, onOtherAccount) {
        const t = requireTransport();
        if (!entitled()) throw new DigesterError('not_entitled', 'Sync needs a Pro plan.');
        if (typeof deviceName !== 'string' || deviceName.trim().length === 0) {
          throw new DigesterError('invalid_input', 'A device name is required', { issues: [{ path: 'deviceName', message: 'Required' }] });
        }
        const account = ctx.repos.localSettings.get('account')?.email ?? null;
        const lastAccount = meta('account');
        if (account !== null && lastAccount !== null && lastAccount !== account) {
          if (onOtherAccount === undefined) {
            throw new DigesterError('conflict', 'This device holds data of another account. Merge it into this account, or replace it?', {
              account: lastAccount,
            });
          }
          if (onOtherAccount === 'replace') {
            db.transaction(() => {
              wipeSyncedTables(db);
              setSyncMeta(db, 'cursor', '0');
              deleteSyncMeta(db, 'epoch');
              deleteSyncMeta(db, 'needs_full_pull');
              deleteSyncMeta(db, 'probe_since');
            });
            notifyWipe();
          } else {
            db.transaction(() => {
              setSyncMeta(db, 'cursor', '0');
              deleteSyncMeta(db, 'epoch');
              deleteSyncMeta(db, 'needs_full_pull');
              deleteSyncMeta(db, 'probe_since');
            });
          }
        }
        const devices = await t.registerDevice({ id: ctx.node as DeviceId, name: deviceName.trim(), platform: platformOf(ctx.capabilities.runtime) });
        db.transaction(() => {
          // Never synced with any replica (first start, merge, replace): send everything once (§11).
          if (meta('epoch') === null) markEverythingDirty(db);
          if (account !== null) setSyncMeta(db, 'account', account);
        });
        started = true;
        setPhase('idle');
        await cycle();
        return devices;
      },

      async stop() {
        started = false;
        setPhase('off');
      },

      async now() {
        await cycle();
        return status();
      },

      async devices() {
        return requireTransport().listDevices();
      },

      async revokeDevice(id) {
        return requireTransport().revokeDevice(id);
      },
    },
    cycle,
  };
}
